// 回放書籤（plan/vms-web-v0.md PLAY-06）：在目前播放點標記時間點或區段，填標題與註記，個人或家戶可見；進全文檢索（事件頁搜得到）。
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../components/Dialog';
import { bookmarkApi } from '../../lib/planeApi';
import { formatDateTime } from '../../lib/tz';
import type { VmsDevice } from '../../lib/devices';

const LENGTHS = [0, 30, 60, 300];

export function BookmarkButton({ dev, at, tz }: { dev: VmsDevice; at: number | null; tz: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [len, setLen] = useState(0);
  const [vis, setVis] = useState<'private' | 'home'>('private');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState<number | null>(null);
  const save = async () => {
    if (start === null) return;
    setBusy(true);
    setErr(null);
    try {
      await bookmarkApi.create(dev.apiEndpoint, dev.homeId, {
        device_ref: dev.aid,
        start_at: new Date(start * 1000).toISOString(),
        end_at: len ? new Date((start + len) * 1000).toISOString() : undefined,
        title: title.trim(),
        note: note.trim() || undefined,
        visibility: vis,
      });
      setOpen(false);
      setTitle('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['bookmarks', dev.homeId] });
    } catch (e) {
      setErr((e as { code?: string }).code ?? String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        className="btn btn-sm"
        disabled={at === null}
        title={at === null ? t('bm.need_time') : undefined}
        onClick={() => {
          setStart(at === null ? null : Math.floor(at));
          setErr(null);
          setOpen(true);
        }}
      >
        🔖 {t('bm.add')}
      </button>
      <Dialog open={open} title={t('bm.add')} onClose={() => setOpen(false)}>
        <p className="muted">
          {dev.name} · {start !== null ? formatDateTime(start, tz, i18n.language) : '—'}
        </p>
        <label className="field">
          <span>{t('bm.title')}</span>
          <input className="input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} aria-label={t('bm.title')} />
        </label>
        <label className="field">
          <span>{t('bm.note')}</span>
          <textarea className="input" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} aria-label={t('bm.note')} />
        </label>
        <label className="field">
          <span>{t('bm.length')}</span>
          <select className="input" value={len} onChange={(e) => setLen(Number(e.target.value))} aria-label={t('bm.length')}>
            {LENGTHS.map((n) => (
              <option key={n} value={n}>
                {n ? t('bm.len_s', { n }) : t('bm.len_point')}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('bm.visibility')}</span>
          <select className="input" value={vis} onChange={(e) => setVis(e.target.value as 'private' | 'home')} aria-label={t('bm.visibility')}>
            <option value="private">{t('bm.vis_private')}</option>
            <option value="home">{t('bm.vis_home')}</option>
          </select>
        </label>
        {err && <p className="error">{t(`bm.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {t('common.save')}
          </button>
        </div>
      </Dialog>
    </>
  );
}

/** 這台攝影機當天的書籤：點了跳到那個時間播放。 */
export function BookmarkList({ dev, from, to, tz, onPick }: { dev: VmsDevice; from: number; to: number; tz: string; onPick(at: number): void }) {
  const { t, i18n } = useTranslation();
  const q = useQuery({
    queryKey: ['bookmarks', dev.homeId, from, to],
    queryFn: () => bookmarkApi.list(dev.apiEndpoint, dev.homeId, new Date(from * 1000).toISOString(), new Date(to * 1000).toISOString()),
    retry: false,
  });
  const list = (q.data ?? []).filter((b) => b.device_ref === dev.aid);
  if (!list.length) return null;
  return (
    <div className="pb-bookmarks">
      <span className="muted small">{t('bm.list')}</span>
      {list.map((b) => (
        <button key={b.bookmark_id} className="chip" title={b.note} onClick={() => onPick(Date.parse(b.start_at) / 1000)} data-bookmark={b.bookmark_id}>
          🔖 {b.title} · {formatDateTime(Date.parse(b.start_at) / 1000, tz, i18n.language)}
          {b.visibility === 'home' ? '' : ` · ${t('bm.vis_private')}`}
        </button>
      ))}
    </div>
  );
}
